/**
 * MasterGanttBulkActions.tsx
 * Follow-up 1.5 — Bulk action bar for the Master Gantt multi-select mode.
 *
 * Rendered inside MasterGanttToolbar when multiSelectMode is ON and at least
 * one bar is selected. Provides two actions:
 *
 *   - Commit Selected: flips all selected Tentative bars to Committed via
 *     POST /api/schedule/commit-bulk. A ConfirmDialog gates the action.
 *     On success the parent refreshes the gantt data and clears the selection.
 *     G2-1 (audit #2): before the dialog opens, the read-only
 *     POST /api/schedule/commit-bulk-check runs against the selected keys and
 *     its advisory bullets (unstaffed bookings, day-off conflicts, hard machine
 *     conflicts, overloaded weeks — the planning page's warning set, key-scoped)
 *     render inside the dialog. Advisory only: the dialog stays confirmable,
 *     and a failed check falls back to the plain confirm.
 *
 *   - Export Selected CSV: filters the full gantt data to only the selected
 *     bars using filterCsvBySelection(), then triggers a browser download.
 *     No confirmation required — immediate, silent operation.
 *
 * Bulk Reschedule is NOT implemented here.
 * TODO (MOFTAKI-FOLLOW-UPS 1.5): Bulk Reschedule — shift all selected bookings
 * by N days via a modal. Deferred because it requires UX decisions on conflict
 * handling (what to show when a rescheduled bar would create a Hard Conflict).
 * Wire the stub button back in here once the spec is decided.
 *
 * Color tokens used:
 *   theme.schedule.committed    — "Commit Selected" button fill
 *   theme.palette.primary.main  — selection chip
 *   theme.border.*              — standard radius / divider
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Check, FileDownload } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { commitScheduleBulk, checkBulkCommit } from '../../../services/scheduleCommit.js';
import { buildSubmitWarnings } from '../../../components/schedule/submitWarnings';
import { SubmitWarningsContent } from '../../../components/schedule/SubmitWarningsContent';
import type { SubmitCheckResult } from '../../../services/scheduleScenario';
import {
  filterCsvBySelection,
  triggerCsvDownload,
} from './masterGanttCsvExport.js';
import type { MasterGanttResponse } from '../../../types/schedule.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MasterGanttBulkActionsProps {
  /** Currently selected booking/assignment keys. */
  selectedKeys: Set<string>;
  /** Full gantt response — used to build the filtered CSV. */
  ganttData: MasterGanttResponse | null;
  /** Called after a successful bulk commit so the parent can refresh + clear. */
  onCommitSuccess: () => void;
  /** Called on any error so the parent can surface an error banner. */
  onError: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Split a Set<string> of mixed booking/assignment keys into two typed arrays.
 *
 * The Master Gantt selection set stores both booking keys and assignment keys
 * in the same Set — we need to route each to the correct backend array.
 *
 * Because the keys are UUIDs and we can't distinguish booking vs assignment
 * from the UUID alone, the ganttData is used as the lookup source:
 *   - If the key appears in machineBookings → bookingKeys[]
 *   - If the key appears in employeeAssignments → assignmentKeys[]
 *
 * Keys not found in either list (stale selection after a refresh) are dropped.
 */
function splitSelectionKeys(
  selectedKeys: Set<string>,
  ganttData: MasterGanttResponse,
): { machineBookingKeys: string[]; employeeAssignmentKeys: string[] } {
  const bookingSet = new Set<string>();
  const assignmentSet = new Set<string>();

  for (const project of ganttData.projects) {
    for (const wo of project.workOrders) {
      for (const b of wo.machineBookings) {
        if (selectedKeys.has(b.bookingKey)) {
          bookingSet.add(b.bookingKey);
        }
      }
      for (const a of wo.employeeAssignments) {
        if (selectedKeys.has(a.assignmentKey)) {
          assignmentSet.add(a.assignmentKey);
        }
      }
    }
  }

  return {
    machineBookingKeys: Array.from(bookingSet),
    employeeAssignmentKeys: Array.from(assignmentSet),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MasterGanttBulkActions: React.FC<MasterGanttBulkActionsProps> = ({
  selectedKeys,
  ganttData,
  onCommitSuccess,
  onError,
}) => {
  const theme = useTheme();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  /** G2-1: pre-commit check in-flight / advisory bullets for the dialog. */
  const [checking, setChecking] = useState(false);
  const [commitWarnings, setCommitWarnings] = useState<string | null>(null);
  /** The check behind the warnings — feeds the structured dialog sections
   *  (SubmitWarningsContent, 2026-08-05). Null when the check came back clean. */
  const [commitWarnCheck, setCommitWarnCheck] = useState<SubmitCheckResult | null>(null);

  // ── Commit Selected ────────────────────────────────────────────────────────

  // G2-1 (audit #2): run the read-only key-scoped commit check first so the
  // confirm dialog can show the same advisory bullets the planning page shows.
  // Advisory only — a failed check opens the plain dialog (never blocks).
  const handleCommitClick = async () => {
    if (checking) return;
    let warnings: string | null = null;
    let warnCheck: SubmitCheckResult | null = null;
    if (ganttData) {
      const { machineBookingKeys, employeeAssignmentKeys } =
        splitSelectionKeys(selectedKeys, ganttData);
      if (machineBookingKeys.length > 0 || employeeAssignmentKeys.length > 0) {
        setChecking(true);
        try {
          const check = await checkBulkCommit({ machineBookingKeys, employeeAssignmentKeys });
          warnings = buildSubmitWarnings(check);
          if (warnings) warnCheck = check;
        } catch {
          warnings = null; // check unavailable — plain confirm
        } finally {
          setChecking(false);
        }
      }
    }
    setCommitWarnings(warnings);
    setCommitWarnCheck(warnCheck);
    setConfirmOpen(true);
  };
  const handleCommitCancel = () => setConfirmOpen(false);

  const handleCommitConfirm = async () => {
    if (!ganttData) {
      setConfirmOpen(false);
      return;
    }

    const { machineBookingKeys, employeeAssignmentKeys } =
      splitSelectionKeys(selectedKeys, ganttData);

    if (machineBookingKeys.length === 0 && employeeAssignmentKeys.length === 0) {
      // All selected keys are gone (stale selection — ganttData was refreshed).
      setConfirmOpen(false);
      return;
    }

    setCommitting(true);
    try {
      await commitScheduleBulk({ machineBookingKeys, employeeAssignmentKeys });
      setConfirmOpen(false);
      onCommitSuccess();
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Bulk commit failed. Please try again.';
      onError(msg);
    } finally {
      setCommitting(false);
    }
  };

  // ── Export Selected CSV ────────────────────────────────────────────────────

  const handleExportCsv = () => {
    if (!ganttData) return;
    const csv = filterCsvBySelection(ganttData, selectedKeys);
    const now = new Date().toISOString().slice(0, 10);
    triggerCsvDownload(csv, `master-gantt-selected-${now}.csv`);
  };

  // ── Count helpers ──────────────────────────────────────────────────────────

  const count = selectedKeys.size;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Action bar — shown inline alongside the selection chip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 0.5,
        }}
      >
        {/* Commit Selected */}
        <Button
          size="small"
          variant="contained"
          startIcon={checking ? <CircularProgress size={14} color="inherit" /> : <Check fontSize="small" />}
          onClick={() => void handleCommitClick()}
          disabled={committing || checking}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '6px',
            backgroundColor: theme.schedule.committed,
            '&:hover': {
              backgroundColor: alpha(theme.schedule.committed, 0.85),
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.schedule.committed, 0.4),
            },
          }}
        >
          Commit Selected
        </Button>

        {/* Export Selected CSV */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownload fontSize="small" />}
          onClick={handleExportCsv}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '6px',
          }}
        >
          Export Selected CSV
        </Button>

        {/*
         * TODO (MOFTAKI-FOLLOW-UPS 1.5): Bulk Reschedule
         * Shifts all selected bookings by N days via a small modal.
         * Deferred — needs UX spec on conflict handling.
         * Uncomment + implement when spec is ready.
         *
         * <Button size="small" variant="outlined" disabled
         *   sx={{ textTransform: 'none', fontSize: '0.75rem' }}
         *   title="Bulk Reschedule — coming soon"
         * >
         *   Reschedule (coming soon)
         * </Button>
         */}
      </Box>

      {/* Confirm Dialog — Bulk Commit */}
      <Dialog
        open={confirmOpen}
        onClose={committing ? undefined : handleCommitCancel}
        maxWidth={commitWarnCheck ? 'sm' : 'xs'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: `${theme.border.radius}px`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: `1px solid ${theme.border.color}`,
            fontWeight: 700,
          }}
        >
          Commit {count} selected item{count !== 1 ? 's' : ''}{commitWarnings ? ' anyway' : ''}?
        </DialogTitle>

        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {/* G2-1: advisory pre-commit sections — same warning set as the
              planning page's Finalize, scoped to the selected keys, rendered
              structured (SubmitWarningsContent, 2026-08-05). */}
          {commitWarnCheck && (
            <Box sx={{ mb: 1.5 }}>
              <SubmitWarningsContent check={commitWarnCheck} />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            All selected <strong>Tentative</strong> machine bookings and
            employee assignments will be flipped to <strong>Committed</strong>.
            Already-committed items are unaffected.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            This action can be reversed by manually editing individual bookings.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${theme.border.color}`,
            gap: 1,
          }}
        >
          <Button
            onClick={handleCommitCancel}
            disabled={committing}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleCommitConfirm()}
            disabled={committing}
            size="small"
            variant="contained"
            startIcon={
              committing ? <CircularProgress size={14} color="inherit" /> : <Check />
            }
            sx={{
              textTransform: 'none',
              backgroundColor: theme.schedule.committed,
              '&:hover': {
                backgroundColor: alpha(theme.schedule.committed, 0.85),
              },
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.schedule.committed, 0.4),
              },
            }}
          >
            {committing ? 'Committing…' : commitWarnings ? 'Commit anyway' : 'Commit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
